/**
 * ผู้ช่วยถาม-ตอบ (AI Assistant) — AI layer A8.
 *
 * Free-text Thai question → the server lets the LLM pick read-tools scoped to
 * the caller's role (no financial tool for non-OWNER), runs them, and answers
 * from the results. If no key is configured the feature reports itself off
 * (Q&A genuinely needs an LLM). Read-only: it answers, it never changes data.
 *
 * Layout: chat-app style. Brand greeting on top, scrollable answer area in
 * the middle, sticky composer at the bottom (sits above BottomTabBar).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Send, Sparkles, Info, ChevronRight,
  FileText, Calendar, Clock3, Camera, User as UserIcon, Search, ListChecks,
} from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { Logo } from '../components/ui/Logo';
import { useAskAI, type AskAIResult } from '../hooks';
import { useAuthStore } from '../lib/store';

// เมนูด่วน — กดไปหน้าตรงๆ ไม่ผ่าน AI (ทำงานได้แม้ไม่ได้ตั้ง AI key).
// แยกเป็น 2 ฝั่ง: พนักงานทุกคน vs admin (HR/Owner).
const QUICK_EMPLOYEE = [
  { to: '/slip', label: 'สลิปเงินเดือน', Icon: FileText },
  { to: '/quota', label: 'สิทธิ์การลาคงเหลือ', Icon: Calendar },
  { to: '/leave', label: 'ขอลา', Icon: Calendar },
  { to: '/ot', label: 'ขอ OT', Icon: Clock3 },
  { to: '/checkin', label: 'เช็คอิน', Icon: Camera },
  { to: '/me/requests', label: 'คำขอของฉัน', Icon: ListChecks },
  { to: '/profile', label: 'ประวัติของฉัน', Icon: UserIcon },
  { to: '/find', label: 'ค้นหาเมนู', Icon: Search },
];

// คำถามแนะนำใต้ composer (ถาม AI จริงๆ — ต้องมี AI key).
const SUGGESTIONS = [
  'ข้อมูลของฉัน',
  'ค่าจ้างของฉัน',
  'สิทธิ์การลาคงเหลือ',
  'คำขอที่ค้างของฉัน',
];

export function AskAI() {
  const [q, setQ] = useState('');
  const [result, setResult] = useState<AskAIResult | null>(null);
  const ask = useAskAI();
  const role = useAuthStore((s) => s.profile?.role ?? 'EMPLOYEE');
  const isAdmin = role === 'OWNER' || role === 'HR' || role === 'MANAGER';

  async function submit(question: string) {
    const text = question.trim();
    if (!text || ask.isPending) return;
    setResult(null);
    setQ(''); // ล้างช่องทันทีหลังกดส่ง — รวมถึงตอนกด quick question chip
    try {
      setResult(await ask.mutateAsync(text));
    } catch (e) {
      setResult({ available: true, answer: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด' });
    }
  }

  return (
    <PageLayout title="ผู้ช่วย AI">
      {/* Reserve room for the sticky composer + quick menu (above BottomTabBar).
          Composer ≈ 56px + quick menu ≈ 44px + tab bar ≈ 64px → ~170-190px. */}
      <div className="pb-48 space-y-4">
        {/* Brand greeting bar — logo + HumanAI wordmark on gradient */}
        <div className="rounded-2xl bg-brand-gradient text-white p-4 shadow-sm">
          <Logo white wordmark size="h-10" />
          <div className="mt-3">
            <div className="font-semibold">พร้อมช่วยเหลือคุณแล้ว</div>
            <div className="text-sm text-white/90 mt-0.5">เลือกเมนูด่วน หรือถาม AI ด้านล่าง</div>
          </div>
        </div>

        {/* เมนูด่วน — กดไปหน้าตรง ไม่ผ่าน AI (ทำงานได้ไม่ต้องตั้ง AI ก่อน) */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">เมนูด่วน</div>
          <div className="grid grid-cols-2 gap-2">
            {QUICK_EMPLOYEE.map((m) => (
              <Link
                key={m.to}
                to={m.to}
                className="card p-3 flex items-center gap-3 hover:shadow-md transition group"
              >
                <div className="w-9 h-9 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                  <m.Icon className="w-4 h-4" />
                </div>
                <div className="text-sm font-medium flex-1 min-w-0 truncate">{m.label}</div>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-brand-600 shrink-0" />
              </Link>
            ))}
            {isAdmin && (
              <Link
                to="/launcher"
                className="card p-3 flex items-center gap-3 hover:shadow-md transition group bg-gradient-to-br from-brand-50/40 to-cyan-50/40"
              >
                <div className="w-9 h-9 rounded-lg bg-brand-600 text-white flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="text-sm font-medium flex-1 min-w-0 truncate">เปลี่ยนแอป HumanAI</div>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-brand-600 shrink-0" />
              </Link>
            )}
          </div>
        </div>

        {ask.isPending && <div className="card p-4 text-sm text-gray-500">กำลังคิด…</div>}

        {result &&
          !ask.isPending &&
          (result.available === false ? (
            <div className="card p-4 flex items-start gap-3 text-sm text-gray-600">
              <Info className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
              <span>{result.message ?? 'ยังไม่ได้เปิดใช้ผู้ช่วย AI'}</span>
            </div>
          ) : (
            <div className={`card p-4 space-y-2 ${result.rate_limited ? 'bg-amber-50 border-amber-200' : ''}`}>
              <div className={`flex items-center gap-2 ${result.rate_limited ? 'text-amber-700' : 'text-brand-600'}`}>
                <Sparkles className="w-4 h-4" />
                <span className="text-xs font-medium">
                  {result.rate_limited ? 'ครบโควต้าวันนี้' : 'คำตอบ'}
                </span>
              </div>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{result.answer ?? 'ไม่พบคำตอบ'}</p>
              {result.tools_used && result.tools_used.length > 0 && (
                <p className="text-xs text-gray-400">ที่มา: {result.tools_used.join(', ')}</p>
              )}
              {result.quota && !result.rate_limited && (
                <p className="text-xs text-gray-400">
                  เหลือถามได้อีก {result.quota.remaining} / {result.quota.limit} ครั้งวันนี้
                </p>
              )}
            </div>
          ))}
      </div>

      {/* Composer + quick menu — sticky bottom, sits above the BottomTabBar.
          z-20 keeps it below the BottomTabBar's z-30 so the tab bar always wins.
          Quick menu (suggestion chips) goes UNDER the input per Owner's request. */}
      <div className="fixed inset-x-0 bottom-[64px] z-20 bg-white border-t border-gray-200 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="max-w-screen-md mx-auto space-y-2">
          <div className="flex gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit(q)}
              placeholder="พิมพ์คำถามเป็นภาษาไทย…"
              className="input flex-1"
            />
            <button
              onClick={() => submit(q)}
              disabled={ask.isPending || !q.trim()}
              aria-label="ส่งคำถาม"
              className="btn-cta flex items-center justify-center w-12 shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>

          {/* Quick menu — horizontally scrollable on small screens */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mb-1">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => submit(s)}
                className="text-xs bg-brand-50 text-brand-700 rounded-full px-3 py-1.5 hover:bg-brand-100 whitespace-nowrap shrink-0"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
